import { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../MainLayout/Header";
import { Box } from "@mui/material";
import SideBar from "../../MainLayout/SideBar";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import { Outlet } from "react-router-dom";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";

// material-ui
import { styled } from "@mui/material/styles";

//Modals
import NewUserM from "../../MainLayout/Modals/NewUserModal";
import LanguageChangeButton from "src/Utils/Translations/LanguageChangeButton";
import { useGetRestaurantList } from "src/data/restaurant/queries/restaurant";
import { useOrganizationRestaurants } from "src/data/organization/queries/organizationRestaurants";
import { SubscriptionExpiryBanner } from "../../MainLayout/SubscriptionExpiryBanner";

const NON_RESTAURANT_SEGMENTS = new Set([
  "statistics",
  "add-restaurant",
  "admin-dashboard",
  "no-restaurant",
]);

export const DRAWER_WIDTH = 255;
export const HEADER_HEIGHT = 75;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  backgroundColor: "#fff",
  height: HEADER_HEIGHT,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Main = styled(Box, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  marginTop: HEADER_HEIGHT,
  backgroundColor: "",
  marginLeft: `calc(${theme.spacing(8)} + 1px)`,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px) `,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

function getWindowDimensions() {
  const { innerWidth: width } = window;
  return {
    width,
  };
}

function PageRestau() {
  const { user, isLoading } = useContext(UserContext);
  const [show, setShow] = useState(false);

  const [closedDrawer, setClosedDrawer] = useState(false);
  const [leftDrawerOpened, setleftDrawerOpened] = useState(true);
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(),
  );

  const {
    restaurantList,
    loading: restaurantListLoading,
    loaded: restaurantListLoaded,
    refetch,
  } = useGetRestaurantList();

  // An account with nothing subscribed may still own a restaurant — see
  // `useOrganizationRestaurants`. Only looked up in that case.
  const {
    restaurants: unsubscribedRestaurants,
    isLoading: unsubscribedRestaurantsLoading,
  } = useOrganizationRestaurants(
    user,
    restaurantListLoaded && restaurantList.length === 0,
  );

  const handleDrawerOpen = () => {
    setleftDrawerOpened(!leftDrawerOpened);
  };
  const handleClose = () => setShow(false);
  let navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (windowDimensions.width < 1250) {
      setleftDrawerOpened(false);
      setClosedDrawer(true);
    } else {
      setleftDrawerOpened(true);
      setClosedDrawer(false);
    }
  }, [windowDimensions]);

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isLoading || user) return;

    navigate(`/login`, { replace: true });
  }, [isLoading, navigate, user]);

  // Managing no restaurant is a resting state, not a failure to heal by
  // refetching — an owner who unsubscribed really has none, and asking again
  // only ever returns the same empty list. There is no id to land on either, so
  // the `*` route under /restaurant takes the bare path to the no-restaurant
  // page on its own.
  useEffect(() => {
    if (restaurantListLoading || restaurantList.length === 0) return;
    const segments = location.pathname.split("/");
    const segment = segments[2];

    const hasRestaurantId = segment && !NON_RESTAURANT_SEGMENTS.has(segment);
    if (!hasRestaurantId) {
      navigate(`/restaurant/${restaurantList[0].id}`, { replace: true });
    }
  }, [restaurantListLoading, restaurantList, location.pathname, navigate]);

  const contextValue = {
    isLoading: restaurantListLoading,
    loaded: restaurantListLoaded,
    restaurants: restaurantList,
    unsubscribedRestaurants,
    unsubscribedRestaurantsLoading,
    refetch,
  };

  return (
    <RestaurantContext.Provider value={contextValue}>
      <LanguageChangeButton />
      <NewUserM show={show} handleClose={handleClose} />
      <CssBaseline />
      <AppBar position="fixed" open={leftDrawerOpened} elevation={0}>
        <Toolbar>
          <Header
            handleDrawerOpen={handleDrawerOpen}
            leftDrawerOpened={leftDrawerOpened}
            closedDrawer={closedDrawer}
            Restaurant_list={restaurantList}
          />
        </Toolbar>
      </AppBar>
      <SideBar
        leftDrawerOpened={leftDrawerOpened}
        setleftDrawerOpened={setleftDrawerOpened}
      />
      {!restaurantListLoading && (
        <Main open={leftDrawerOpened}>
          <SubscriptionExpiryBanner topOffset={HEADER_HEIGHT} />
          <Box
            sx={{
              padding: { xs: 1, sm: 2, md: 4 },
              backgroundColor: "rgb(245, 247, 250)",
              minHeight: `calc(98vh - ${HEADER_HEIGHT}px)`,
            }}
          >
            <Outlet />
          </Box>
        </Main>
      )}
    </RestaurantContext.Provider>
  );
}

export default PageRestau;
